import type { Dispatch } from 'redux';
import { type ActionsObject, makeAction, makeReducer } from '@finn-no/redux-actions';
import { debounce } from 'ts-debounce';

import { type AccommodationState, ApiActionStatus, type Filters, type HotelPageState, type ThunkResult } from '@/interfaces';
import { getHotel } from '@/utils/fetch/kayak/kayakFetcher';
import { defaultState } from './defaultState';
import { updateHotelPageQueryParams } from '@/utils/url/routerHelpers';

const MAX_FETCH_ATTEMPTS = 8;

export const actions: ActionsObject<HotelPageState> = {};

actions.clearHotel = makeAction(
    'HOTEL_PAGE/CLEAR',
    (state: HotelPageState): HotelPageState => ({
        ...state,
        results: [],
    }),
);

actions.setHotelPending = makeAction(
    'HOTEL_PAGE/HOTEL_PENDING',
    (state: HotelPageState): HotelPageState => ({
        ...state,
        status: ApiActionStatus.PENDING,
        attempts: state.attempts + 1,
    }),
);

actions.setHotel = makeAction(
    'HOTEL_PAGE/HOTEL_OK',
    (state, action): HotelPageState => ({
        ...defaultState.hotelPage,
        ...action.payload,
        status: ApiActionStatus.OK,
        hotelKey: action.payload.key,
        isComplete: action.payload.isComplete || state.attempts >= MAX_FETCH_ATTEMPTS,
    }),
);

actions.setHotelError = makeAction(
    'HOTEL_PAGE/HOTEL_ERROR',
    (state: HotelPageState): HotelPageState => ({
        ...state,
        status: ApiActionStatus.ERROR,
        isComplete: true,
    }),
);

export const debouncedFetchHotel = debounce(async (filters: Filters, dispatch: Dispatch): Promise<void> => {
    const { status, data } = await getHotel(filters);
    if (status === 200) {
        dispatch(actions.setHotel(data));
    } else {
        dispatch(actions.setHotelError());
    }
}, 300);

export const fetchHotel =
    (): ThunkResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => AccommodationState): Promise<void> => {
        dispatch(actions.setHotelPending());
        dispatch(actions.clearHotel());
        const filters = getState().filters;
        await updateHotelPageQueryParams(filters);
        debouncedFetchHotel(filters, dispatch);
    };

export const pollHotel =
    (): ThunkResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => AccommodationState): Promise<void> => {
        dispatch(actions.setHotelPending());
        debouncedFetchHotel(getState().filters, dispatch);
    };

export const hotelPageReducer = makeReducer<HotelPageState>(actions);
